<template>
  <div class="c2c">

    <!-- Chargement -->
    <div v-if="loading" class="text-xs-center">
      <v-progress-circular indeterminate class="primary--text" :size="100" :width="1"></v-progress-circular>
    </div>

    <div v-else>
      <v-card class="mb-3">
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs6 md3>
              <div class="ml-4 grey--text text--darken-2">{{ $t('successfulCalls') | cap }}</div>
              <div class="ml-4 headline teal--text">{{ nbCalls }}</div>
            </v-flex>
            <v-flex xs6 md3>
              <div class="ml-4 grey--text text--darken-2">{{ $t('missedCalls') | cap }}</div>
              <div class="ml-4 headline blue--text">{{ nbMissed }}</div>
            </v-flex>
            <v-flex xs6 md3>
              <div class="ml-4 grey--text text--darken-2">{{ $t('totalDurationOfCalls') | cap }}</div>
              <div class="ml-4 headline deep-purple--text">{{ totalDuration | duration}}</div>
            </v-flex>
            <v-flex xs6 md3>
              <div class="ml-4 grey--text text--darken-2">{{ $t('averageDurationOfACall') | cap }}</div>
              <div class="ml-4 headline deep-orange--text">{{ (totalDuration / nbCalls) | duration }}</div>
            </v-flex>
          </v-layout>
          <highcharts :options="options"></highcharts>
        </v-card-text>
      </v-card>
      <v-card class="mb-3">
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs6 sm10 class="headline">{{ $t('callsDetails') | cap }}</v-flex>
            <v-flex xs6 sm2>
              <v-btn
              color="primary"
              dark
              slot="activator"
              flat
              @click="download()"
              :disabled="callsDetails.length == 0"
              >
                {{ $t('exportInCsv') }}
                <v-icon color="primary" right>file_download</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-data-table
            :headers="headers"
            :items="callsDetails"
            :no-data-text="$tc('calls', 0) | cap"
            :no-results-text="$tc('results', 0) | cap"
            :rows-per-page-items="[10, 25, 50, { text: $tc('all', 2), value: -1 }]"
            :rows-per-page-text="$t('callsPerPage') | cap"
          >
            <template slot="headerCell" slot-scope="props">
              <span>{{ props.header.text }}</span>
            </template>
            <template slot="items" slot-scope="props">
              <td>{{ props.item.date | date }}</td>
              <td>{{ props.item.time }}</td>
              <td class="text-xs-right">{{ props.item.duration | duration }}</td>
              <td>{{ props.item.appelant }}</td>
              <td>{{ props.item.appele }}</td>
              <td>{{ props.item.site }}</td>
              <td>{{ (props.item.duration === '0') ? $t('yes') : $t('no') }}</td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters } from 'vuex'
import json2csv from 'json2csv'

/**
 * Constantes et fonctions servant
 * à formater une durée/date
 */
const durationFormat = 'h [h] mm [min] ss [s]'
function duration (v) {
  return moment.duration(_.toNumber(v), 'seconds').format(durationFormat)
}

const dateFormat = 'ddd D MMM YY'
function date (v) {
  return moment(v).format(dateFormat)
}

export default {

  name: 'C2C',

  data () {
    return {
      loading: false,
      periode: '',
      c2c: {},
      options: {
        chart: {
          height: 400
        },
        title: {
          text: '',
          x: -20 // center
        },
        tooltip: {
          formatter: function () { // formatage de secondes à une durée en heures
            var s = '<b>' + this.x + '</b>'
            _.each(this.points, function (point) {
              s += '<br/><span style="color: ' + point.series.color + '">' + point.series.name + '</span>: <b>'
              s += (point.series.index > 1)
              ? moment.duration(point.y, 'seconds').format(durationFormat)
              : point.y
              s += '</b>'
            })
            return s
          },
          // pointFormat: '<span style="color: {series.color}">{series.name}</span>',
          shared: true,
          // useHTML: true,
          // headerFormat: '<small>{point.x}</small><ul>',
          // pointFormat: '<li style="color: {series.color}">{series.name}: ' +
          //     '<span style="text-align: right"><b>{point.y} EUR</b></span></li>',
          // footerFormat: '</ul>',
          borderColor: '#cccccc',
          crosshairs: true
        },
        xAxis: {
          categories: [] // à remplir par l'appel ajax
        },
        yAxis: [
          { // petites valeurs entières
            allowDecimals: false,
            labels: {
              format: '{value}',
              style: {
                color: 'rgba(0, 150, 136, 1)'
              }
            },
            title: {
              text: this.$t('successfulCalls'),
              style: {
                color: 'rgba(0, 150, 136, 1)'
              }
            },
            opposite: false
          }, { // grandes valeurs entières
            allowDecimals: false,
            labels: {
              format: '{value}',
              style: {
                color: '#9C27B0'// purple
              }
            },
            title: {
              text: this.$t('totalDurationOfCalls') + ' (sec.)',
              style: {
                color: '#9C27B0'// purple
              }
            },
            opposite: true
          }
        ],
        series: [
          {
            name: _.upperFirst(this.$t('successfulCalls')),
            type: 'column',
            color: 'rgba(0, 150, 136, 0.8)', // teal
            marker: {symbol: 'circle', enabled: false},
            visible: true,
            tooltip: { valueSuffix: '' },
            yAxis: 0,
            data: [] // à remplir par l'appel ajax
          },
          {
            name: _.upperFirst(this.$t('missedCalls')),
            type: 'column',
            color: 'rgba(33, 150, 243, 0.8)', // blue
            marker: {symbol: 'circle', enabled: false},
            visible: true,
            tooltip: { valueSuffix: '' },
            yAxis: 0,
            data: [] // à remplir par l'appel ajax
          },
          {
            name: _.upperFirst(this.$t('totalDurationOfCalls')),
            type: 'spline',
            color: '#9C27B0', // purple
            marker: {symbol: 'circle', enabled: false},
            visible: true,
            tooltip: { valueSuffix: '' },
            yAxis: 1,
            data: [] // à remplir par l'appel ajax
          },
          {
            name: _.upperFirst(this.$t('averageDurationOfACall')),
            type: 'spline',
            color: '#FF5722', // deep-orange
            marker: {symbol: 'circle', enabled: false},
            visible: true,
            tooltip: { valueSuffix: '' },
            yAxis: 1,
            data: [] // à remplir par l'appel ajax
          }
        ],
        exporting: {
          enabled: true
        },
        navigation: {
          buttonOptions: {
            verticalAlign: 'bottom',
            y: -15,
            x: -20
          }
        },
        credits: {
          enabled: false
        }
      },
      /**
       * variables liées au tableau du détails des apples
       */
      headers: [
        { text: 'Date', value: 'date', align: 'left' },
        { text: _.upperFirst(this.$t('hour')), value: 'time', align: 'left' },
        { text: _.upperFirst(this.$t('duration')), value: 'duration' },
        { text: _.upperFirst(this.$t('appellant')), value: this.$t('appelant'), align: 'left' },
        { text: _.upperFirst(this.$t('called')), value: 'appele', align: 'left' },
        { text: _.upperFirst(this.$tc('sites', 1, {count: ''})), value: 'site', align: 'left' },
        { text: _.upperFirst(this.$t('missed')) + '?', value: 'state', align: 'left' }
      ],
      callsDetails: []
    }
  },

  computed: {
    ...mapGetters('_Stats', ['params']),
    ...mapGetters('_Sites', ['GET_SITE_BY_KEY']),
    nbCalls () {
      return _.sumBy(this.c2c.days, 'calls')
    },
    nbMissed () {
      return _.sumBy(this.c2c.days, 'missed')
    },
    totalDuration () {
      return _.sumBy(this.c2c.days, 'duration')
    }
  },

  mounted () {
    this.getStatsC2C()
    // la période affiché
    let paramsArray = this.params.split('=')
    let debut = paramsArray[1].substring(0, 10)
    let fin = paramsArray[2].substring(0, 10)
    this.periode = '_du_' + debut + '_au_' + fin + '.csv'
    // console.log('periode', this.periode)
  },

  methods: {
    getStatsC2C () {
      this.loading = true
      this.axios.get('get/c2c.php?' + this.params + '&entKey=' + this.$store.state._User.entKey)
      .then(res => {
        this.c2c = res.data
        this.options.xAxis.categories = []
        this.options.series[0].data = []
        this.options.series[1].data = []
        this.options.series[2].data = []
        this.options.series[3].data = []
        _.each(this.c2c.days, day => {
          this.options.xAxis.categories.push(moment(day.date).format(dateFormat))
          this.options.series[0].data.push(day.calls)
          this.options.series[1].data.push(day.missed)
          this.options.series[2].data.push(day.duration)
          this.options.series[3].data.push((day.calls === 0) ? 0 : (day.duration / day.calls))
        })
        this.callsDetails = this.c2c.calls
        _.each(this.callsDetails, call => {
          let site = this.GET_SITE_BY_KEY(call.lic)
          call.site = site ? site.LIC_URL : ''
        })
      })
      .catch(err => {
        console.error(err)
      })
      .then(() => {
        this.loading = false
      })
    },

    download () {
      let csv = null
      let fields = _.map(this.c2c.calls[0], (v, k) => k)
      fields.push('missed')
      // ajout de toute les données
      let dataToExport = []
      for (let i = 0; i < this.c2c.calls.length; i++) {
        let value = {}
        _.each(this.c2c.calls[i], (v, k) => {
          switch (k) {
            case 'duration':
              value[k] = duration(v)
              break
            case 'date':
              value[k] = date(v)
              break
            case 'lic':
              let site = this.GET_SITE_BY_KEY(v)
              value[k] = site ? site.LIC_URL : ''
              break
            default:
              value[k] = v
          }
        })
        value.missed = this.c2c.calls[i]['duration'] > 0
        dataToExport.push(value)
      }
      try {
        csv = json2csv({ data: dataToExport, del: ';', fields: fields })
      } catch (err) {
        return
      }
      // téléchargement du fichier csv
      var hiddenElement = document.createElement('a')
      hiddenElement.href = 'data:attachment/text,' + encodeURI(csv)
      hiddenElement.target = '_blank'
      hiddenElement.download = 'callbacks' + this.periode + '.csv'
      document.body.appendChild(hiddenElement)
      hiddenElement.click()
    }
  },

  filters: {
    duration: function (v) {
      return duration(v)
    },
    date: function (v) {
      return date(v)
    }
  },

  watch: {
    params (v) {
      // La période affiché
      let paramsArray = v.split('=')
      let debut = paramsArray[1].substring(0, 10)
      let fin = paramsArray[2].substring(0, 10)
      this.periode = '_du_' + debut + '_au_' + fin + '.csv'

      this.getStatsC2C()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/stats/C2C.vue